/* import __COLOCATED_TEMPLATE__ from './part-select-grid.hbs'; */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import Comparator from '@ebiz/ember-aditools-device-table/utils/comparator';
import EngineeringFilter from '@ebiz/ember-aditools-device-table/utils/engineering-filter';
import EngineeringRangeValueFilter from '@ebiz/ember-aditools-device-table/utils/engineering-range-value-filter';
import PriceFilter from '@ebiz/ember-aditools-device-table/utils/price-filter';
import {
  convertColumnDataForSelectionTable,
  convertDeviceDataForSelectionTable,
  reduceColumnFilterValuesToThosePresent,
} from '@ebiz/ember-aditools-device-table/utils/device-table-pst';
import { getMaxTempRange } from '../utils/error-utils';
import DacData from '../data/dacdata';
import { modifier } from 'ember-modifier';

const options = {
  enableCellNavigation: true,
  enableColumnReorder: true,
  multiColumnSort: true,
  multiSelect: false,
  showHeaderRow: true,
  headerRowHeight: 30,
  frozenColumn: 0,
  explicitInitialization: true,
  parametersPanel: true,
};

const filterOptionKeys = {
  vodac: 'Voltage Output DAC',
  mdac: 'Multiplying DAC',
  csdac: '4-20mA Current Loop DAC',
  cssdac: 'Current Source/Sink DAC',
  fpdac: 'Fast Precision DAC',
  vref: 'Voltage Reference',
  niamp: 'Non-Inverting Amplifier',
  nibuf: 'Non-Inverting Buffer',
  iamp: 'Inverting Amplifier',
  ibuf: 'Inverting Buffer',
  oamp: 'Output Amplifier',
  obuf: 'Output Buffer',
};

export default class PartSelectGrid extends Component {
  @service intl;
  @service('aditools-site') site;
  @service dataService;
  @tracked _columns;
  @tracked updatedfilters;
  @tracked filters;
  @tracked sortColumns;
  @tracked pstColumns = [];
  @tracked parameterSelectorGrid = false;
  @tracked checkedColumns;

  ////////////////////////////////////////////////////////////////////////////////
  // Properties                                                                 //
  ////////////////////////////////////////////////////////////////////////////////
  get data() {
    let rows = this.getSelectionRows();
    return rows;
  }
  get columns() {
    return (
      this._columns ??
      this.pstColumns.filter((x) => {
        return x.isDefault;
      })
    );
  }
  set columns(cols) {
    this._columns = cols;
  }
  get options() {
    return options;
  }
  get selectedItem() {
    return this.args.selectedItem.name;
  }
  get showDeviceTable() {
    return this.pstColumns.length > 0;
  }
  get tableRowSummary() {
    let labelSummary = 'opamp';
    if (this.args.type === this.intl.t(`labels.parts.voltage_reference`)) {
      labelSummary = 'vref';
    } else if (this.args.type.includes('DAC')) {
      labelSummary = 'dac';
    }
    return this.intl.t('labels.modal.part-selection.row-count.' + labelSummary).toString();
  }
  get gridType() {
    return Object.keys(filterOptionKeys).find((key) => filterOptionKeys[key] === this.args.type);
  }
  get noColumnsSelected() {
    return this.checkedColumns !== undefined && this.checkedColumns.length === 1;
  }

  ////////////////////////////////////////////////////////////////////////////////
  // Component Actions                                                          //
  ////////////////////////////////////////////////////////////////////////////////
  inserting = modifier(() => {
    if (this.args.handleOnShow) {
      this.args.handleOnShow();
      this.pstColumns = this.getSelectionColumns();
      reduceColumnFilterValuesToThosePresent(this.data, this.pstColumns);
      if (this.args.model.selectionFilterOptions[this.gridType]) {
        if (this.args.model.selectionFilterOptions[this.gridType].columns) {
          this.columns = this.args.model.selectionFilterOptions[this.gridType].columns;
        }
        if (this.args.model.selectionFilterOptions[this.gridType].filters) {
          this.filters = this.args.model.selectionFilterOptions[this.gridType].filters;
        }
        if (this.args.model.selectionFilterOptions[this.gridType].sortColumns) {
          this.sortColumns = this.args.model.selectionFilterOptions[this.gridType].sortColumns;
        }
      }
    }
  });

  @action onChooseParameters() {
    this.parameterSelectorGrid = true;
  }

  @action cancelParameterSelector() {
    this.parameterSelectorGrid = false;
    this.checkedColumns = undefined;
  }

  @action onAllParameters() {
    this.columns = this.pstColumns;
    if (this.updatedfilters) {
      this.filters = [...this.updatedfilters];
    }
    this.args.model.updateFilterOptions(this.gridType, this.columns, this.filters, this.sortColumns);
  }

  @action onResetParameters() {
    this.columns = this.pstColumns.filter((x) => {
      return x.isDefault;
    });
    this.updatedfilters = undefined;
    this.filters = undefined;
    this.sortColumns = undefined;
    this.args.model.updateFilterOptions(this.gridType, this.columns, this.filters, this.sortColumns);
  }

  @action submitParameterSelector(closeDialog) {
    if (this.checkedColumns) {
      this.columns = [...this.checkedColumns];
    }
    if (this.updatedfilters) {
      this.filters = [...this.updatedfilters];
    }
    this.args.model.updateFilterOptions(this.gridType, this.columns, this.filters, this.sortColumns);
    this.parameterSelectorGrid = false;
    if (closeDialog) {
      closeDialog();
    }
  }

  @action selectionChanged(newColumns) {
    this.checkedColumns = [...newColumns];
  }

  @action filterChanged(filters) {
    // 1. Get filters for any hidden columns
    const filtersOfHiddenCols = [];
    if (this.updatedfilters) {
      this.updatedfilters.forEach((filterCol) => {
        const result = this.columns.find((col) => {
          return col.id === filterCol.columnId;
        });
        if (!result) {
          filtersOfHiddenCols.push(filterCol);
        }
      });
    }
    // 2. Merge filters for visible and hidden columns
    this.updatedfilters = filters.concat(filtersOfHiddenCols);
    this.args.model.updateFilterOptions(this.gridType, this.columns, this.updatedfilters, this.sortColumns);
  }

  @action sortChanged(sortColumns) {
    this.sortColumns = sortColumns;
    this.args.model.updateFilterOptions(
      this.gridType,
      this.columns,
      this.filters === undefined ? this.updatedfilters : this.filters,
      this.sortColumns,
    );
  }

  ////////////////////////////////////////////////////////////////////////////////
  // Support Functions                                                          //
  ////////////////////////////////////////////////////////////////////////////////
  getSelectionColumns() {
    console.trace('getSelectionColumns - view: ', this.args.view);

    console.log('view', this.args.view.columns);

    const pstColumns = this.args.view.columns.map(
      (column) =>
        Object.assign(
        {},
        column,
        convertColumnDataForSelectionTable(column, this.site.AdiLocale),
      )
    );

    console.log('pstColumns', pstColumns);

    let self = this;
    let partFormatter = function (row, cell, value, columnDef, dataContext) {
      return [
        '<span class="',
        value.toLowerCase(),
        '-part-number-cell part-number-cell">',
        value,
        '<a target="_blank"',
        'class="recommended-part-item"',
        'rel="noopener noreferrer"',
        'data-action="part"',
        'data-partid="',
        value,
        '"',
        ' href="',
        self.site.SiteUrl,
        '/',
        self.site.AdiLocale,
        '/',
        dataContext['generic'],
        '">',
        '<span class="link-icon"></span>',
        '</a>',
        '</span>',
      ].join('');
    };

    if (this.args.type === 'Voltage Reference') {
      pstColumns.push({
        id: 'x1',
        name: 'Initial+TC',
        field: 'display_x1',
        sortable: true,
        comparator: Comparator.engineeringComparator,
        filter: new EngineeringRangeValueFilter('', ''),
        classification: 'Other',
        isDefault: true,
        unit: 'ppm',
        minMaxTyp: null,
        toolTip: 'Initial Error + TC Error (ppm)',
      });
    }

    let typeColumnNames = [];
    let typeColumnWidths = [];
    switch (this.gridType) {
      case 'vodac':
        typeColumnNames = DacData.psts.voltageOutputDac.supportedParameters;
        typeColumnWidths = DacData.psts.voltageOutputDac.pstDisplayColumnWidths;
        break;
      case 'mdac':
        typeColumnNames = DacData.psts.multiplyingDac.supportedParameters;
        typeColumnWidths = DacData.psts.multiplyingDac.pstDisplayColumnWidths;
        break;
      case 'csdac':
        typeColumnNames = DacData.psts.currentSourceDac.supportedParameters;
        typeColumnWidths = DacData.psts.currentSourceDac.pstDisplayColumnWidths;
        break;
      case 'cssdac':
        typeColumnNames = DacData.psts.currentSourceSinkDac.supportedParameters;
        typeColumnWidths = DacData.psts.currentSourceSinkDac.pstDisplayColumnWidths;
        break;
      case 'fpdac':
        typeColumnNames = DacData.psts.fastPrecisionDac.supportedParameters;
        typeColumnWidths = DacData.psts.fastPrecisionDac.pstDisplayColumnWidths;
        break;
      case 'vref':
        typeColumnNames = DacData.psts.voltageReference.supportedParameters;
        typeColumnWidths = DacData.psts.voltageReference.pstDisplayColumnWidths;
        break;
      case 'niamp':
      case 'nibuf':
      case 'iamp':
      case 'ibuf':
      case 'oamp':
      case 'obuf':
        typeColumnNames = DacData.psts.operationalAmplifiers.supportedParameters;
        typeColumnWidths = DacData.psts.operationalAmplifiers.pstDisplayColumnWidths;
        break;
    }

    pstColumns.forEach((item) => {
      // Set Column Widths
      item.minWidth =
        typeColumnNames.indexOf(item.id) >= 0 && typeColumnWidths[typeColumnNames.indexOf(item.id) + 1] > 0
          ? typeColumnWidths[typeColumnNames.indexOf(item.id) + 1]
          : 110;
      item.width =
        typeColumnNames.indexOf(item.id) >= 0 && typeColumnWidths[typeColumnNames.indexOf(item.id) + 1] > 0
          ? typeColumnWidths[typeColumnNames.indexOf(item.id) + 1]
          : item.name
            ? item.name.length * 9
            : 110;
      if (item.id === 'partNumber') {
        item.minWidth = typeColumnWidths[0] > 0 ? typeColumnWidths[0] : item.minWidth;
        item.width = typeColumnWidths[0] > 0 ? typeColumnWidths[0] : item.width + 100;
        item.resizable = false;
        item.formatter = partFormatter;
      }

      // Set filter type for engineering and price filters
      if (item.filter && item.filter instanceof EngineeringFilter) {
        item.filter = new EngineeringRangeValueFilter('', '');
      } else if (item.filter && item.filter instanceof PriceFilter) {
        item.filter = new PriceFilter('LT');
      }
    });

    return pstColumns;
  }

  getSelectionRows() {
    const maxTempRange = getMaxTempRange(this.args.model);
    const deviceData = convertDeviceDataForSelectionTable(
      this.args.data.data,
      this.pstColumns,
      true,
    );

    for (let i = 0; i < deviceData.length; i++) {
      const deviceDataItem = deviceData[i];
      if (this.args.type === 'Voltage Reference') {
        const initialError = deviceDataItem['value_initialAccuracyMax'] * 10000; // id 5071 - Initial Accuracy % (max)
        const voutTCError = deviceDataItem['value_voutTempcoMaxppmC'] * maxTempRange; // id 4997 - Vout Tempco (max)
        const totalError = initialError + voutTCError;
        deviceDataItem['value_x1'] = totalError;
        deviceDataItem['display_x1'] = Math.round(totalError).toString();
      }
      deviceDataItem['disallowed'] = false;
      deviceDataItem['selectable'] = true;
      deviceData[i] = deviceDataItem;
    }
    return deviceData;
  }
}
